import { useCallback } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import {
  ShutdownBannerEvent,
  shutdownBannerEventFromJSON,
  ShutdownBannerEventType,
} from '@repo/sdk/models/components';

import { useSupportsShutdown } from '@/hooks/query-hooks/gaia-api/shutdown-banner';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useStream } from '@/hooks/use-stream';
import { shutdownBannerStreamQuery } from '@/queries/gaia-api/shutdown-banner-stream-query';
import { STREAM_TYPES } from '@/state/stream-status.state';

export const useShutdownBannerStream = () => {
  const { agentId } = useCurrentFacility();
  const { data: supportsShutdown } = useSupportsShutdown({ agentId });

  const queryClient: QueryClient = useQueryClient();
  const { webSocketUrl, queryKey: shutdownBannerKey, enabled } = shutdownBannerStreamQuery(agentId);

  const handleShutdownBannerEvent = useCallback(
    (event: ShutdownBannerEvent) => {
      switch (event.type) {
        case ShutdownBannerEventType.Keepalive: {
          // Keep Alive is a keep alive message.
          // Maybe: A single keepalive cache for all sockets?
          console.debug(`Shutdown Banner keep alive at ${new Date().toISOString()}`);
          break;
        }
        case ShutdownBannerEventType.ShutdownBanner: {
          // Replace the shutdown banner state.
          queryClient.setQueryData(shutdownBannerKey, () => event.shutdownStatus);
          break;
        }
        default:
          break;
      }
    },
    [queryClient, shutdownBannerKey]
  );

  // Only open the stream if the agent supports shutdown and the stream is enabled (agentId is defined)
  return useStream<ShutdownBannerEvent>(enabled && supportsShutdown ? webSocketUrl : null, {
    onReceiveContent: handleShutdownBannerEvent,
    parse: shutdownBannerEventFromJSON,
    streamType: STREAM_TYPES.SHUTDOWN_BANNER,
    displayName: 'Shutdown Banner',
  });
};
